import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add, Delete } from "@mui/icons-material";
import MDButton from "components/MDButton"; // Assuming your custom button path
import { useAuth } from "context/authContext"; // Adjust path if needed

// --- CUSTOM HOOKS & CONFIGS ---
import { useImportDependencies } from "./hooks/useImportDependencies";
import { useImportValidation } from "./hooks/useImportValidation";
import { IMPORT_STRATEGIES } from "./hooks/importStrategies";
import { importData } from "services/api";
import ImportProgressModal from "./ImportProgressModal";

const ImportReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authData = useAuth(); // Contains user info if needed for dependencies

  // 1. Get Initial State
  const importedData = location.state?.importedData || [];
  const dataType = location.state?.dataType || "unknown";
  const companyId = location.state?.companyId || null;

  // 2. UI State
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [importLoading, setImportLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [progress, setProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "info" });

  // 3. HOOK: Fetch Dependencies (Jobs, Departments, etc.)
  const { loading: depsLoading, contextData } = useImportDependencies(
    dataType,
    location.state,
    authData
  );

  // 4. HOOK: Handle Validation & Grid State
  const {
    rows,
    columns: strategyColumns,
    errors,
    hasBlockingErrors,
    handleAddRow,
    handleDeleteRow,
    processRowUpdate,
  } = useImportValidation(importedData, dataType, contextData, depsLoading);

  // 5. Construct Final DataGrid Columns
  // We take the columns from the Strategy and wrap them to show Red Borders/Text on error
  const gridColumns = useMemo(() => {
    // A. Map Strategy Columns to add Error Styling
    const styledColumns = strategyColumns.map((col) => ({
      ...col,
      renderCell: (params) => {
        // Check for specific field error OR general row error
        const errorMsg = errors[params.id]?.[params.field] || errors[params.id]?.["_general"];

        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              px: 1,
              bgcolor: errorMsg ? "rgba(255, 0, 0, 0.1)" : "inherit",
              border: errorMsg ? "1px solid red" : "none",
              borderRadius: 1,
              position: "relative",
            }}
          >
            {params.value}
            {errorMsg && (
              <Tooltip title={errorMsg} placement="top">
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: 6,
                    height: 6,
                    bgcolor: "red",
                    borderRadius: "50%",
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    }));

    // B. Append Action Column (Delete)
    return [
      ...styledColumns,
      {
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        renderCell: (params) => (
          <IconButton size="small" color="error" onClick={() => handleDeleteRow(params.id)}>
            <Delete fontSize="small" />
          </IconButton>
        ),
      },
    ];
  }, [strategyColumns, errors, handleDeleteRow]);

  const handleConfirmImport = async () => {
    // 1. Validation
    if (Object.keys(errors).length > 0) {
      setSnackbar({ open: true, msg: "Please fix errors before importing.", severity: "warning" });
      return;
    }

    if (rows.length === 0) {
      setSnackbar({ open: true, msg: "No data to import.", severity: "warning" });
      return;
    }

    // 2. Start Loading (This opens the Modal)
    setImportLoading(true);
    setProgress(0);
    setLoadingMessage("Preparing data...");

    try {
      const strategy = IMPORT_STRATEGIES[dataType];
      if (!strategy || !strategy.mapToDTO) {
        throw new Error("No DTO mapping strategy found");
      }

      // A. Transform Rows
      const fullContext = { ...contextData, companyId };
      const transformedData = rows.map((row) => strategy.mapToDTO(row, fullContext));

      // B. Chunked Upload Logic
      const totalRows = transformedData.length;
      let processed = 0;
      const chunkSize = 50;

      for (let i = 0; i < totalRows; i += chunkSize) {
        const chunk = transformedData.slice(i, i + chunkSize);

        // Update Modal Message
        setLoadingMessage(`Importing rows ${i + 1}-${Math.min(i + chunkSize, totalRows)}...`);

        // Call API
        const res = await importData(dataType, chunk);
        if (res === "error") throw new Error(`Failed to import batch ${i}`);

        // Update Modal Progress Bar
        processed += chunk.length;
        setProgress((processed / totalRows) * 100);
      }

      // C. Success & Navigation
      setLoadingMessage("Finalizing...");
      await new Promise((r) => setTimeout(r, 500)); // Small delay for UX

      setSnackbar({ open: true, msg: "Data uploaded successfully!", severity: "success" });

      // Delay navigation slightly so user sees 100% completion
      setTimeout(() => {
        if (dataType === "kpis" || dataType === "scorecards") {
          navigate("/performance");
        } else {
          const viewMap = {
            areas: 0,
            branches: 1,
            jobs: 2,
            employees: 3,
            departments: 4,
            jobDetails: 2,
          };
          navigate("/organization", { state: { view: viewMap[dataType] ?? 0 } });
        }
      }, 1000);
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, msg: "An error occurred during import.", severity: "error" });
      // Only close loading if error occurs (so they can read the error),
      // or close immediately depending on preference.
      setImportLoading(false);
    }
    // Note: We don't put setImportLoading(false) in 'finally' here
    // because we want the modal to stay visible until navigation happens on success.
  };

  // --- RENDER ---

  if (depsLoading) {
    return (
      <Box sx={{ height: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading dependencies...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", p: 2, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
        Reviewing {dataType.charAt(0).toUpperCase() + dataType.slice(1)} Data
      </Typography>
      {dataType === "scorecards" && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
          Date Format: Day/Month/Year or Day-Month-Year
        </Typography>
      )}

      {/* Global Validation Alert */}
      {hasBlockingErrors && (
        <Alert severity="error" sx={{ mb: 2 }}>
          There are errors in the data. Rows with red borders contain invalid data.
        </Alert>
      )}

      {/* Progress Bar (Overlay) */}
      {importLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {loadingMessage} ({Math.round(progress)}%)
        </Alert>
      )}

      {/* Grid Container */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <DataGrid
          rows={rows}
          columns={gridColumns}
          processRowUpdate={processRowUpdate}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          loading={importLoading}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell:focus": { outline: "none" },
          }}
        />

        {/* Floating Add Button */}
        <IconButton
          onClick={handleAddRow}
          disabled={importLoading}
          sx={{
            position: "absolute",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: "primary.main",
            color: "white",
            boxShadow: 4,
            "&:hover": { bgcolor: "primary.dark" },
            "&:disabled": { bgcolor: "grey.400" },
          }}
        >
          <Add />
        </IconButton>
      </Box>

      {/* Footer / Confirm Button */}
      <Box sx={{ mt: 3, mb: 2, display: "flex", justifyContent: "center" }}>
        <MDButton
          onClick={handleConfirmImport}
          color="primary"
          variant="contained"
          disabled={hasBlockingErrors || importLoading || rows.length === 0}
          sx={{ width: 300, py: 1.5, fontSize: "16px" }}
        >
          {importLoading ? <CircularProgress size={24} color="inherit" /> : "Confirm Import"}
        </MDButton>
      </Box>

      <ImportProgressModal open={importLoading} progress={progress} message={loadingMessage} />

      {/* Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ImportReview;
